import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Points</h3>
    <p className="text-[#b8b8b8] mb-2">
      Points are awarded to players for their positioning in the charts at the
      end of the age. The top 50 of the players in each chart are awarded
      points. Extra points are given to the top three players in each of the
      Province and Race charts and every kingdom province for the top 3 Kingdom
      awards. Points at the kingdom level are awarded to all members of the
      Kingdom. First place for each category gets 50 points, reducing down to 1
      point for 50th place.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Any players or kingdoms which are tied for position at the end of the age,
      share the rewards for that position. For example, if three kingdoms all
      shared first place, then they would each get a Gold Crown and the 4th
      placed kingdom would get a Silver Crown, 5th would get Bronze etc.
    </p>
    <p className="text-[#b8b8b8] mb-2">Points are accumulated after each age.</p>
  </div>
);

const CategoriesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Categories</h3>
    <p className="text-[#b8b8b8] mb-2">
      Points are awarded for the following categories. 1st place recieves 50
      points down to 50th place that recieves 1 point.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
          Kingdom Awards
        </h4>
        <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
          <li>Kingdom Land</li>
          <li>Kingdom Honour</li>
          <li>Kingdom War Points</li>
        </ul>
      </div>
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
          Province Awards
        </h4>
        <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
          <li>Province Land</li>
          <li>Province Honour</li>
        </ul>
      </div>
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
          Race Awards
        </h4>
        <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
          <li>Race Land</li>
          <li>Race Honour</li>
        </ul>
      </div>
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
          Achievement Points
        </h4>
        <div className="space-y-4">
          <div>
            <h5 className="text-yellow-300 mb-1">Trophy Points</h5>
            <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
              <li>1st - 60pts</li>
              <li>2nd - 30pts</li>
              <li>3rd - 15pts</li>
            </ul>
          </div>
          <div>
            <h5 className="text-yellow-300 mb-1">Medal Points</h5>
            <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
              <li>1st - 40pts</li>
              <li>2nd - 30pts</li>
              <li>3rd - 20pts</li>
            </ul>
          </div>
          <div>
            <h5 className="text-yellow-300 mb-1">Crown Points</h5>
            <ul className="text-[#b8b8b8] list-disc list-inside space-y-1">
              <li>1st - 40pts</li>
              <li>2nd - 30pts</li>
              <li>3rd - 20pts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AwardsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Awards</h3>
    <p className="text-[#b8b8b8] mb-2">
      There are several awards which are given out to players at the end of each
      age. Each award goes to the top three in each category, Gold, Silver and
      Bronze.
    </p>
    <div className="space-y-6">
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Crowns</h4>
        <p className="text-[#b8b8b8]">
          Awarded to all players in each of the top three kingdoms for Land,
          honour and war points.
        </p>
      </div>

      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Medals</h4>
        <p className="text-[#b8b8b8]">
          Awarded to each of the top three players for each race for Land and
          honour.
        </p>
      </div>

      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Trophies</h4>
        <p className="text-[#b8b8b8]">
          Awarded to each of the top three players for Land and Honour.
        </p>
      </div>
    </div>
  </div>
);

export const Route = createFileRoute('/charts/')({
  component: ChartsPage,
});

function ChartsPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/charts" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/charts@2x.png"
              alt="Charts"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Charts</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <CategoriesSection />
            <AwardsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
